import { Config } from 'config/envConfig';
import React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
    faCircleXmark,
    faExclamationTriangle,
    faFileImport,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { alert, AlertType, Button, ConfirmModal } from '@quyva/shared-ui';
import { getUrl } from '../../../utils';
import { AllowedFileExtensions } from './importTemplateOrLayout';
import { checkIfFileExists, readFileContent, validateFile } from './utils';

export const ImportTemplateView = ({
    selectedFiles,
    setSelectedFiles,
    templateList,
    setTemplateList,
    error,
    setError,
    fetchTemplateList,
    setActiveTemplate,
    setViewImportModal,
    user,
    setLoadingText,
    setIsLoading,
}) => {
    const [viewConfirmModal, setViewConfirmModal] = useState(false);

    // Validate file types
    const validateTemplateFiles = (files) => {
        if (files && files.length) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileName = file.name;

                if (fileName.toLowerCase().startsWith('default_')) {
                    setError('Template name can not start with default_');
                    return false;
                }

                const fileNameParts = fileName.split('.');
                const fileExtension = fileNameParts[fileNameParts.length - 1];
                const fileExtensionInLowerCase = fileExtension.toLowerCase();
                const validFileNameRegex = /^[a-zA-Z0-9_.]*$/;
                const isValid = validFileNameRegex.test(fileName);
                if (
                    fileExtensionInLowerCase !== AllowedFileExtensions.CSV &&
                    fileExtensionInLowerCase !== AllowedFileExtensions.PY &&
                    !isValid
                ) {
                    setError(
                        'Only CSV and py files are allowed also File name can only contain letters, numbers, underscores, and dots.'
                    );
                    return false;
                }
                if (
                    fileExtensionInLowerCase !== AllowedFileExtensions.CSV &&
                    fileExtensionInLowerCase !== AllowedFileExtensions.PY
                ) {
                    setError('Only CSV and py files are allowed.');
                    return false;
                }

                if (!isValid) {
                    setError(
                        'File name can only contain letters, numbers, underscores, and dots.'
                    );
                    return false;
                }
            }
        } else {
            setError('No File.');
            return false;
        }
        return true;
    };

    const uploadTemplate = async () => {
        try {
            setLoadingText('Importing Template...');
            setIsLoading(true);

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}`
            );
            const data = { id: user.email };
            const data2 = JSON.stringify(data);
            const encodedToken = encodeURIComponent(user.token);
            const formData = new FormData();

            selectedFiles.forEach((file, index) => {
                formData.append(`file`, file);
            });
            await axios.post(`${dynamicUrl}/upload222/${data2}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: encodedToken,
                },
            });
            // Handle success
            alert(AlertType.SUCCESS, 'Template uploaded successfully');

            setSelectedFiles([]);
            fetchTemplateList(user, setActiveTemplate, setTemplateList);
        } catch (error) {
            // Handle error
            alert(AlertType.ERROR, error.message);
        } finally {
            setIsLoading(false);
            setLoadingText('');
            setViewImportModal(false);
        }
    };
    // Handle form submission
    const handleSubmit = async () => {
        setError('');

        if (!selectedFiles || selectedFiles.length === 0) {
            setError('Please select a file.');
            return;
        }

        const isValid = validateTemplateFiles(selectedFiles);
        if (!isValid) return;

        for (const file of selectedFiles) {
            const fileName = file.name; // Get the name of the file

            const { isNameWithExtensionSame, isNameWithoutExtensionSame } =
                checkIfFileExists(fileName, templateList);

            if (isNameWithoutExtensionSame && !isNameWithExtensionSame) {
                // display error that a template with same name but different extension already exists.
                setError(
                    'Template with same name but different extension already exists.'
                );
                return;
            }

            const fileContent = await readFileContent(file);
            const validationResponse = await validateFile(
                fileName,
                fileContent,
                setError
            );

            if (!validationResponse.isValid) {
                // setError(`File validation failed for :  ${fileName}`);
                return;
            }

            if (isNameWithExtensionSame && isNameWithoutExtensionSame) {
                // update the template
                setViewConfirmModal(true);
                return;
            }
        }
        await uploadTemplate();
    };

    return (
        <div className='flex flex-col justify-center'>
            {viewConfirmModal && (
                <ConfirmModal
                    modalText={`A Template with name '${selectedFiles[0].name}' already exists. Do you want to update the existing template?`}
                    onConfirm={async () => {
                        await uploadTemplate();
                        setViewConfirmModal(false);
                        setViewImportModal(false);
                    }}
                    onCancel={() => {
                        setSelectedFiles([]);
                        setViewConfirmModal(false);
                    }}
                />
            )}

            <div className='min-h-[20px] flex justify-center items-center text-buttons-alert400 mt-3'>
                {error && (
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className='mr-2'
                    />
                )}
                {error &&
                    error.split('\n').map((line, index) => (
                        <div key={index} className='text-buttons-alert400'>
                            {line}
                        </div>
                    ))}
            </div>

            <div className='flex items-center justify-center mt-4'>
                <div className='flex items-center justify-between w-full'>
                    <Button
                        type='danger'
                        icon={faCircleXmark}
                        className='w-1/3'
                        text={'Cancel'}
                        hoverText={'Cancel and close the modal'}
                        onClick={() => {
                            setViewImportModal(false);
                        }}
                    />
                    <Button
                        type='success'
                        icon={faFileImport}
                        className='w-1/3'
                        text={'Import'}
                        hoverText={'Import template'}
                        onClick={async () => {
                            await handleSubmit();
                        }}
                        disabled={error || selectedFiles.length === 0}
                    />
                </div>
            </div>
        </div>
    );
};
